'use client';

import { useState } from 'react';
import { AlertCircle, Search } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { relativeTime } from '@/lib/ops/format';
import { STATE_LABEL, STATE_TONE } from '@/lib/ops/support';
import type { CaseState } from '@/lib/server/support/cases';

/**
 * Guest status lookup (tracker row 3.7).
 *
 * The pair is the credential: a reference and the email given at intake. The API
 * answers a uniform 404 for a wrong ref, a wrong email and an unknown case, so the
 * screen has one failure message and does not confirm which half was wrong.
 */

interface StatusCase {
  ref: string;
  type: string;
  label: string;
  state: CaseState;
  summary: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  resolutionCode: string | null;
  slaFirstResponseAt: string;
  slaResolveAt: string;
}

interface StatusMessage {
  id: string;
  authorKind: 'requester' | 'agent' | 'system';
  body: string;
  channel: string;
  createdAt: string;
}

export default function GuestStatusLookup() {
  const [ref, setRef] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [found, setFound] = useState<{ case: StatusCase; messages: StatusMessage[]; responseWindow: string } | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFound(null);
    try {
      const response = await fetch('/api/public/support/status', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ref: ref.trim(), email: email.trim() }),
      });
      const payload = (await response.json().catch(() => null)) as
        | { ok?: boolean; error?: string; case?: StatusCase; messages?: StatusMessage[]; responseWindow?: string }
        | null;
      if (!response.ok || !payload?.ok || !payload.case) {
        setError(payload?.error || 'We could not find a request with that reference and email.');
        return;
      }
      setFound({
        case: payload.case,
        messages: payload.messages ?? [],
        responseWindow: payload.responseWindow ?? 'the published window',
      });
    } catch {
      setError('We could not reach the support desk. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <form className="space-y-4" onSubmit={submit} noValidate>
        <div className="space-y-2">
          <Label htmlFor="status-ref">Reference</Label>
          <Input
            id="status-ref"
            value={ref}
            onChange={(event) => setRef(event.target.value)}
            placeholder="S-01024"
            autoComplete="off"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="status-email">Email you used</Label>
          <Input
            id="status-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
        </div>
        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>No match</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <Button type="submit" disabled={busy || !ref.trim() || !email.trim()}>
          <Search className="mr-2 h-4 w-4" aria-hidden="true" />
          {busy ? 'Looking…' : 'Check progress'}
        </Button>
      </form>

      {found ? (
        <div className="space-y-5 border-t pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-lg font-semibold">{found.case.label}</h2>
            <Badge variant={STATE_TONE[found.case.state]}>{STATE_LABEL[found.case.state]}</Badge>
            <span className="font-mono text-xs text-muted-foreground">{found.case.ref}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Filed {relativeTime(found.case.createdAt)}. First reply target: {found.responseWindow} from filing.
          </p>
          <div className="rounded-lg border bg-muted/30 p-4 text-sm">
            <p className="font-medium">What you told us</p>
            <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{found.case.summary}</p>
          </div>

          {found.messages.length ? (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Conversation</h3>
              <ul className="space-y-3">
                {found.messages.map((message) => (
                  <li
                    key={message.id}
                    className={
                      message.authorKind === 'requester'
                        ? 'ml-auto max-w-[85%] rounded-lg bg-primary/10 p-3 text-sm'
                        : 'mr-auto max-w-[85%] rounded-lg border bg-card p-3 text-sm'
                    }
                  >
                    <p className="mb-1 text-xs text-muted-foreground">
                      {message.authorKind === 'agent'
                        ? 'Menuraq Support'
                        : message.authorKind === 'system'
                          ? 'Automatic receipt'
                          : 'You'}{' '}
                      · {relativeTime(message.createdAt)}
                    </p>
                    <p className="whitespace-pre-wrap">{message.body}</p>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nothing has been sent yet. A person will reply by email or text within the window above.
            </p>
          )}

          <p className="text-xs text-muted-foreground">
            Reply to the email or text and it lands on this case. If you did not get one, check the address or phone
            number you gave us.
          </p>
        </div>
      ) : null}
    </div>
  );
}
